import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount } from 'wagmi';
import '../styles/retro-terminal.css';

interface NavigationProps {
  currentPage: string;
  onPageChange: (page: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({ currentPage, onPageChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { address } = useAccount();
  
  // Check if user is admin
  const LEXI_WALLET = '0x7A550FB0a013f3e905470b5EA98cdB3316F2c0b6';
  const isAdmin = address && address.toLowerCase() === LEXI_WALLET.toLowerCase();

  const menuItems = [
    { id: 'chat', label: 'Chat with LEXI', icon: '💬' },
    { id: 'explorer', label: 'Oracle Explorer', icon: '🔍' },
    { id: 'suggest', label: 'Suggest Facts', icon: '💡' },
    ...(isAdmin ? [{ id: 'approval', label: 'Approve Facts', icon: '✅' }] : []),
    { id: 'governance', label: 'Vote', icon: '🗳️' },
  ];

  const handlePageChange = (page: string) => {
    onPageChange(page);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          LEXI Oracle
        </Typography>
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={currentPage === item.id}
              onClick={() => handlePageChange(item.id)}
            >
              <ListItemIcon>
                <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static" className="retro-terminal-bg" sx={{ bgcolor: '#000', color: '#00ff00', borderBottom: '2px solid #00ff00' }}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 2, color: '#00ff00' }}
            >
              ☰
            </IconButton>
          )}
          
          <Typography 
            variant="h6" 
            component="div" 
            className="retro-terminal retro-terminal-text"
            sx={{ 
              flexGrow: isMobile ? 0 : 1,
              fontFamily: "'Courier Prime', 'Courier New', monospace",
              fontSize: '18px',
              fontWeight: 700,
              color: '#00ff00',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              textShadow: '0 0 5px #00ff00'
            }}
          >
            {'>'} LEXI ORACLE KNOWLEDGE SYSTEM
          </Typography>

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1, ml: 4 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.id}
                  className="retro-terminal retro-terminal-text"
                  onClick={() => handlePageChange(item.id)}
                  sx={{
                    fontFamily: "'Courier Prime', 'Courier New', monospace",
                    fontSize: '12px',
                    fontWeight: currentPage === item.id ? 700 : 400,
                    color: currentPage === item.id ? '#00ff00' : '#00aa00',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    border: currentPage === item.id ? '1px solid #00ff00' : '1px solid transparent',
                    '&:hover': {
                      color: '#00ff00',
                      border: '1px solid #00ff00',
                      backgroundColor: 'rgba(0, 255, 0, 0.1)',
                    }
                  }}
                >
                  {item.icon} {item.label.toUpperCase()}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ ml: 'auto' }}>
            <ConnectButton 
              accountStatus="full"
              chainStatus="icon"
              showBalance={false}
            />
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navigation;
